import * as z from "zod/v4";

const text = z.string().trim().min(1).max(4000);
const timestamp = z.iso.datetime({ offset: true });
const url = z.url().refine(value => /^https?:\/\//.test(value), "Use an HTTP or HTTPS source URL.");
const newSourceUrl = url.max(4096, "Source URLs must be at most 4096 characters.");
const evidenceId = z.string().startsWith("research-evidence-").max(200);
const forecastId = z.string().startsWith("research-forecast-").max(200);

export const researchEvidenceInputSchema = z.object({
  title: text.max(240), url: newSourceUrl, excerpt: text, interpretation: text,
  publishedAt: timestamp.nullable(),
  publicationDate: z.iso.date().nullable().default(null),
}).strict();
export type ResearchEvidenceInput = z.infer<typeof researchEvidenceInputSchema>;
export type ResearchEvidence = ResearchEvidenceInput & {
  kind: "evidence"; id: string; capturedAt: string; digest: string;
};

export const researchForecastInputSchema = z.object({
  title: text.max(240), eventId: text.max(160),
  mode: z.enum(["prospective", "replay"]),
  cutoffAt: timestamp, startsAt: timestamp, dueAt: timestamp,
  instrument: z.enum(["BTC-USD", "MARA"]),
  priceDefinition: text.max(600), resolutionSource: newSourceUrl,
  probabilityBps: z.number().int().min(0).max(10000),
  baselineProbabilityBps: z.number().int().min(0).max(10000),
  baselineRationale: text, modelVersion: text.max(240),
  mechanism: text, falsifier: text,
  evidenceIds: z.array(evidenceId).min(1).max(30),
}).strict();
export type ResearchForecastInput = z.infer<typeof researchForecastInputSchema>;
export type ResearchForecast = ResearchForecastInput & {
  kind: "forecast"; id: string; frozenAt: string; evidence: ResearchEvidence[];
  outcomeRule: "end-price-strictly-above-start-price"; digest: string;
  voidPolicy: "owner-reported-invalid-observation-or-unresolvable-target";
};

export const researchResolutionInputSchema = z.object({
  forecastId,
  startPrice: z.number().positive().finite(), endPrice: z.number().positive().finite(),
  // This must exactly match a persisted forecast's source. Keep legacy long
  // URLs resolvable; new source registration uses the bounded schema above.
  sourceUrl: url, sourceExcerpt: text,
}).strict();
export type ResearchResolutionInput = z.infer<typeof researchResolutionInputSchema>;
export type ResearchResolution = ResearchResolutionInput & {
  kind: "resolution"; id: string; resolvedAt: string; forecastDigest: string;
  outcome: 0 | 1; verification: "owner-reported"; digest: string;
};
export const researchVoidInputSchema = z.object({
  forecastId,
  reason: z.enum(["source-unavailable", "invalid-observation", "target-ambiguous"]), detail: text,
}).strict();
export type ResearchVoid = z.infer<typeof researchVoidInputSchema> & {
  kind: "void"; id: string; recordedAt: string; forecastDigest: string; digest: string;
};
export type ResearchRecord = ResearchEvidence | ResearchForecast | ResearchResolution | ResearchVoid;
export type ResearchLedger = { records: ResearchRecord[]; generatedAt: string };

export class ResearchIntegrityError extends Error {}

// Budget the complete stored document with headroom below Firestore limits;
// D1 follows the same bound so TEST cannot accept an unportable cloud record.
export const RESEARCH_RECORD_MAX_STORED_BYTES = 750_000;

export function researchRecordStorageDocument(ownerId: string, record: ResearchRecord) {
  const stored = { owner_id: ownerId, payload_json: JSON.stringify(record) };
  const bytes = new TextEncoder().encode(JSON.stringify(stored)).byteLength;
  if (bytes > RESEARCH_RECORD_MAX_STORED_BYTES) {
    throw new ResearchIntegrityError("This research record exceeds the 750 KB storage budget. Frozen forecasts include copies of every selected evidence snapshot. Select fewer or shorter evidence excerpts before freezing.");
  }
  return stored;
}

function requireCondition(condition: boolean, message: string): asserts condition {
  if (!condition) throw new ResearchIntegrityError(message);
}

function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  if (value !== null && typeof value === "object") {
    return `{${Object.entries(value).sort(([a], [b]) => a.localeCompare(b)).map(([key, item]) => `${JSON.stringify(key)}:${canonical(item)}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

async function digest(value: unknown) {
  const bytes = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(canonical(value)));
  return Array.from(new Uint8Array(bytes), byte => byte.toString(16).padStart(2, "0")).join("");
}

export async function verifyResearchRecord(record: ResearchRecord) {
  const { digest: expected, ...content } = record;
  return expected === await digest(content);
}

export async function captureResearchEvidence(input: unknown, now = new Date().toISOString()): Promise<ResearchEvidence> {
  const parsed = researchEvidenceInputSchema.parse(input);
  requireCondition(parsed.publishedAt === null || Date.parse(parsed.publishedAt) <= Date.parse(now), "Evidence publication cannot be in the future.");
  requireCondition(parsed.publicationDate === null || parsed.publicationDate <= now.slice(0, 10), "Evidence publication date cannot be in the future.");
  const content = { ...parsed, kind: "evidence" as const, id: `research-evidence-${crypto.randomUUID()}`, capturedAt: now };
  return { ...content, digest: await digest(content) };
}

export async function freezeResearchForecast(input: unknown, records: ResearchRecord[], now = new Date().toISOString()): Promise<ResearchForecast> {
  const parsed = researchForecastInputSchema.parse(input);
  const cutoff = Date.parse(parsed.cutoffAt), start = Date.parse(parsed.startsAt), due = Date.parse(parsed.dueAt), clock = Date.parse(now);
  requireCondition(cutoff <= clock && cutoff <= start && start < due, "Use a past information cutoff, followed by the target start and end.");
  requireCondition(parsed.mode !== "prospective" || clock < start, "A prospective forecast must be frozen before the target starts. Use replay for historical development.");
  requireCondition(new Set(parsed.evidenceIds).size === parsed.evidenceIds.length, "Do not repeat an evidence record.");
  const evidence = parsed.evidenceIds.map(id => records.find((record): record is ResearchEvidence => record.kind === "evidence" && record.id === id));
  requireCondition(evidence.every((record): record is ResearchEvidence => Boolean(record)), "An evidence snapshot is missing from this owner's ledger.");
  for (const record of evidence) {
    requireCondition(await verifyResearchRecord(record), "An evidence snapshot failed its integrity check.");
    if (parsed.mode === "prospective") {
      requireCondition(Date.parse(record.capturedAt) <= cutoff, "Evidence first captured after the information cutoff cannot enter a prospective forecast.");
    } else {
      // With only a civil publication date, use the end of that date in UTC-12
      // (the latest possible timezone) as a conservative availability bound.
      const availableBy = record.publishedAt !== null ? Date.parse(record.publishedAt)
        : record.publicationDate ? Date.parse(`${record.publicationDate}T00:00:00Z`) + 36 * 3600000 : Infinity;
      requireCondition(availableBy <= cutoff, "Replay requires publication before the cutoff. Date-only evidence uses a conservative end-of-date bound; historical system access remains unverified.");
    }
  }
  const content = { ...parsed, kind: "forecast" as const, id: `research-forecast-${crypto.randomUUID()}`,
    frozenAt: now, evidence: structuredClone(evidence), outcomeRule: "end-price-strictly-above-start-price" as const,
    voidPolicy: "owner-reported-invalid-observation-or-unresolvable-target" as const };
  return { ...content, digest: await digest(content) };
}

export async function resolveResearchForecast(input: unknown, records: ResearchRecord[], now = new Date().toISOString()): Promise<ResearchResolution> {
  const parsed = researchResolutionInputSchema.parse(input);
  const forecast = records.find((record): record is ResearchForecast => record.kind === "forecast" && record.id === parsed.forecastId);
  requireCondition(forecast !== undefined, "The forecast is missing from this owner's ledger.");
  requireCondition(await verifyResearchRecord(forecast), "The frozen forecast failed its integrity check.");
  requireCondition(!records.some(record => record.kind === "resolution" && record.forecastId === forecast.id), "An outcome has already been recorded. Resolutions cannot be overwritten.");
  requireCondition(!records.some(record => record.kind === "void" && record.forecastId === forecast.id), "This forecast has been voided. Its record remains in the ledger.");
  requireCondition(Date.parse(now) >= Date.parse(forecast.dueAt), "Wait until the declared target window has ended before recording its outcome.");
  requireCondition(parsed.sourceUrl === forecast.resolutionSource, "Resolve against the source declared in the frozen forecast.");
  const content = { ...parsed, kind: "resolution" as const, id: `research-resolution-${forecast.id}`,
    resolvedAt: now, forecastDigest: forecast.digest, outcome: (parsed.endPrice > parsed.startPrice ? 1 : 0) as 0 | 1,
    verification: "owner-reported" as const };
  return { ...content, digest: await digest(content) };
}

export async function voidResearchForecast(input: unknown, records: ResearchRecord[], now = new Date().toISOString()): Promise<ResearchVoid> {
  const parsed = researchVoidInputSchema.parse(input);
  const forecast = records.find((record): record is ResearchForecast => record.kind === "forecast" && record.id === parsed.forecastId);
  requireCondition(forecast !== undefined && await verifyResearchRecord(forecast), "A valid frozen forecast is required.");
  requireCondition(Date.parse(now) >= Date.parse(forecast.dueAt), "Wait until the target window has ended before reporting an unresolvable or invalid observation.");
  requireCondition(!records.some(record => record.kind === "void" && record.forecastId === forecast.id), "This forecast has already been voided.");
  const content = { ...parsed, kind: "void" as const, id: `research-void-${forecast.id}`, recordedAt: now, forecastDigest: forecast.digest };
  return { ...content, digest: await digest(content) };
}

export function scoreResearchLedger(records: ResearchRecord[]) {
  const forecasts = records.filter((record): record is ResearchForecast => record.kind === "forecast");
  const resolutions = records.filter((record): record is ResearchResolution => record.kind === "resolution");
  const prospective = forecasts.filter(record => record.mode === "prospective");
  const voids = records.filter((record): record is ResearchVoid => record.kind === "void");
  const isVoided = (forecast: ResearchForecast) => voids.some(record => record.forecastId === forecast.id && record.forecastDigest === forecast.digest);
  const resolved = prospective.flatMap(forecast => {
    if (isVoided(forecast)) return [];
    const resolution = resolutions.find(record => record.forecastId === forecast.id && record.forecastDigest === forecast.digest);
    return resolution ? [{ forecast, resolution }] : [];
  });
  const mean = (values: number[]) => values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
  const modelBrier = mean(resolved.map(({ forecast, resolution }) => (forecast.probabilityBps / 10000 - resolution.outcome) ** 2));
  const baselineBrier = mean(resolved.map(({ forecast, resolution }) => (forecast.baselineProbabilityBps / 10000 - resolution.outcome) ** 2));
  const voidCount = prospective.filter(isVoided).length;
  return { prospectiveCount: prospective.length, resolvedCount: resolved.length, pendingCount: prospective.length - resolved.length - voidCount, voidCount,
    replayCount: forecasts.length - prospective.length, eventCount: new Set(resolved.map(row => row.forecast.eventId)).size,
    modelBrier, baselineBrier, improvement: modelBrier === null || baselineBrier === null ? null : baselineBrier - modelBrier,
    status: "edge-unproven" as const, outcomeVerification: "owner-reported" as const };
}
